import React from 'react';
import { X, ExternalLink } from 'lucide-react';
import { Exercise } from '../types';

interface ViewExerciseModalProps {
  isOpen: boolean;
  onClose: () => void;
  exercise: Exercise | null;
}

const ViewExerciseModal: React.FC<ViewExerciseModalProps> = ({
  isOpen,
  onClose,
  exercise,
}) => {
  if (!isOpen || !exercise) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Заголовок */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {exercise.name}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Содержимое */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Левая колонка - изображение и основная информация */}
            <div>
              {/* Изображение упражнения */}
              {exercise.image_url ? (
                <div className="mb-6">
                  <img
                    src={exercise.image_url}
                    alt={exercise.name}
                    className="w-full h-64 object-cover rounded-lg shadow-md"
                  />
                </div>
              ) : (
                <div className="mb-6 w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center">
                  <span className="text-gray-400 text-lg">Нет изображения</span>
                </div>
              )}

              {/* Видео упражнения */}
              {exercise.video_url && (
                <div className="mb-6">
                  <video
                    src={exercise.video_url}
                    controls
                    className="w-full h-64 object-cover rounded-lg shadow-md"
                  >
                    Ваш браузер не поддерживает видео.
                  </video>
                </div>
              )}

              {/* Основная информация */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Основная информация</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Название:</span>
                      <p className="text-gray-900">{exercise.name}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Группы мышц:</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {exercise.muscle_groups?.map((group) => (
                          <span
                            key={group.id}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {group.name}
                          </span>
                        ))}
                        {(!exercise.muscle_groups || exercise.muscle_groups.length === 0) && (
                          <span className="text-gray-500 text-sm">Не указаны</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Дата создания:</span>
                      <p className="text-gray-900">
                        {new Date(exercise.created_at).toLocaleDateString('ru-RU')}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Создатель:</span>
                      <p className="text-gray-900">
                        {exercise.created_by_trainer?.first_name} {exercise.created_by_trainer?.last_name}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Публичный доступ:</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ml-2 ${
                        exercise.is_public 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {exercise.is_public ? 'Доступно всем' : 'Только для вас'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Медиа ссылки */}
                {(exercise.image_url || exercise.video_url) && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Медиа материалы</h3>
                    <div className="space-y-2">
                      {exercise.image_url && (
                        <a
                          href={exercise.image_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          <ExternalLink size={16} className="mr-2" />
                          Открыть изображение
                        </a>
                      )}
                      {exercise.video_url && (
                        <a
                          href={exercise.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          <ExternalLink size={16} className="mr-2" />
                          Открыть видео
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Правая колонка - описание и инструкции */}
            <div className="space-y-6">
              {/* Описание */}
              {exercise.description && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Описание</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{exercise.description}</p>
                  </div>
                </div>
              )}

              {/* Инструкции */}
              {exercise.instructions && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Инструкции по выполнению</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="prose prose-sm max-w-none">
                      <p className="text-gray-700 whitespace-pre-wrap">{exercise.instructions}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Если нет описания и инструкций */}
              {!exercise.description && !exercise.instructions && (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-2">
                    <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">📝</span>
                    </div>
                  </div>
                  <p className="text-gray-500">Описание и инструкции не добавлены</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Кнопки */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewExerciseModal; 